/**
 * Which roles open each tab of the pages with role-dependent tabs. Kept outside the (lazy) pages so
 * links elsewhere — the Agent overview, the settings search — only offer tabs the viewer can open.
 * Services enforce the same rules server-side.
 */
import { routeOf, routesFor } from "../routes";

export const ADMIN_TABS = {
  tools: ["tool-admin", "platform-admin"],
  tenant: ["tenant-admin", "platform-admin"],
  users: ["tenant-admin", "platform-admin"],
  // A tenant's own approval amounts (ADR-0063); platform admins set the platform's in "platform".
  approvals: ["tenant-admin"],
  platform: ["platform-admin"],
  // One place for internet access: model provider addresses and the browser's websites.
  internet: ["platform-admin", "tool-admin"],
  // An organisation's own websites for the web browser (ADR-0096); builders see them in the Builder.
  web: ["tenant-admin", "platform-admin"],
  // Command Centre profiles of the organisation (ADR-0117).
  command: ["tenant-admin"],
  // Spending limits (ADR-0063) are settings, so they live here, not on an observe page.
  spending: ["operator", "finance", "tenant-admin"],
} as const;

/** Health and usage: the operations detail and how requests flow (formerly the Operations page). */
export const HEALTH_TABS = {
  overview: [],
  details: ["operator", "finance", "tenant-admin"],
  flow: ["operator", "finance", "tenant-admin"],
} as const;

export const CHANNEL_TABS = {
  desk: ["channel-agent", "channel-supervisor"],
  settings: ["channel-admin", "channel-agent", "channel-supervisor"],
  widgets: ["channel-admin"],
  // The platform team sets up voice for platform agents (the padel courts finder, ADR-0070).
  voice: ["channel-admin", "platform-admin"],
  connect: ["channel-admin"],
  notify: ["channel-campaigner"],
  consents: ["channel-consent-admin"],
} as const;

const TAB_ROLES: Record<string, Record<string, readonly string[]>> = { "/admin": ADMIN_TABS, "/channels": CHANNEL_TABS, "/health": HEALTH_TABS };

/** The viewer can open this link: its page, and its `?tab=` where the page has role-dependent tabs. */
export function canOpen(roles: readonly string[], path: string): boolean {
  const url = new URL(path, "http://x");
  const route = routeOf(url.pathname);
  if (!route || !routesFor(roles).some((r) => r.key === route.key)) return false;
  const tab = url.searchParams.get("tab");
  const tabs = TAB_ROLES[route.path];
  if (!tab || !tabs) return true;
  const need = tabs[tab];
  return !need || need.length === 0 || need.some((r) => roles.includes(r));
}
